import { useRef, useState } from "react";
import { FileSignature, Download, UploadCloud, ShieldCheck, ShieldAlert, PenLine, Eraser } from "lucide-react";
import { api, fmtError } from "../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : null;

const SignaturePad = ({ onSigned, onCancel }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#f59e0b";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    canvasRef.current.getContext("2d").clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasInk(false);
  };
  const submit = async () => {
    if (!hasInk) {
      toast.error("Dessinez votre signature dans le cadre");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await api.post("/studio/contract/sign", { signature_png_base64: dataUrl });
      toast.success("Contrat signé électroniquement — merci !");
      onSigned?.();
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 border border-white/15 bg-black/50 p-4" data-testid="signature-pad">
      <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-zinc-400">
        Signez dans le cadre (souris ou doigt) — vaut « lu et approuvé »
      </p>
      <canvas
        ref={canvasRef}
        width={520}
        height={200}
        data-testid="signature-canvas"
        className="w-full touch-none border border-dashed border-amber-500/40 bg-ink"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <div className="mt-3 flex gap-3">
        <button onClick={clear} data-testid="signature-clear" className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-white">
          <Eraser className="h-3.5 w-3.5" /> Effacer
        </button>
        <button onClick={submit} disabled={busy} data-testid="signature-submit" className="flex items-center gap-2 bg-amber-500 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
          <PenLine className="h-3.5 w-3.5" /> {busy ? "Signature…" : "Valider la signature"}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-500 hover:text-white">
          Annuler
        </button>
      </div>
    </div>
  );
};

export const StudioContract = ({ user, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [signMode, setSignMode] = useState(false);
  const fileRef = useRef(null);

  if (user.role !== "artist") return null;

  const received = user.contract_status === "received";
  const approvedAt = user.approved_at ? new Date(user.approved_at) : null;
  const due = approvedAt ? new Date(approvedAt.getTime() + 30 * 24 * 3600 * 1000) : null;
  const overdue = due && !received && new Date() > due;

  const download = async () => {
    try {
      const res = await api.get("/studio/contract.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Contrat_LaConsoleDesArtistes.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Contrat téléchargé — signez-le et renvoyez-le ici");
      onChanged?.();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/studio/contract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contrat signé reçu — merci !");
      onChanged?.();
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className={`border p-8 ${received ? "border-emerald-500/30 bg-emerald-500/5" : overdue ? "border-red-500/40 bg-red-500/5" : "border-amber-500/30 bg-amber-500/5"}`}
      data-testid="studio-contract"
    >
      <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
        <FileSignature className="h-5 w-5 text-amber-500" /> Contrat de diffusion
      </h2>
      {received ? (
        <p className="mt-4 flex items-start gap-3 text-sm text-emerald-300" data-testid="contract-ok">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          Contrat signé reçu — votre situation est en règle. Merci !
        </p>
      ) : (
        <>
          <p className={`mt-4 flex items-start gap-3 text-sm ${overdue ? "text-red-300" : "text-amber-200/90"}`} data-testid="contract-due">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
            {due
              ? overdue
                ? `Le délai d'un mois est dépassé (depuis le ${fmtDate(due.toISOString())}) — vos morceaux sont masqués jusqu'à réception du contrat signé.`
                : `À renvoyer signé avant le ${fmtDate(due.toISOString())} (1 mois après validation). Passé ce délai, vos morceaux seront masqués du catalogue.`
              : "Téléchargez, signez et renvoyez votre accord de principe dans un délai d'un mois."}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              onClick={download}
              data-testid="contract-download"
              className="flex items-center gap-2 border border-amber-500/60 px-5 py-3 text-xs uppercase tracking-[0.2em] text-amber-400 transition-colors hover:bg-amber-500 hover:text-black"
            >
              <Download className="h-4 w-4" /> Télécharger le contrat
            </button>
            <button
              onClick={() => setSignMode(true)}
              data-testid="contract-sign-online"
              className="flex items-center gap-2 border border-emerald-500/60 px-5 py-3 text-xs uppercase tracking-[0.2em] text-emerald-400 transition-colors hover:bg-emerald-500 hover:text-black"
            >
              <PenLine className="h-4 w-4" /> Signer en ligne
            </button>
            <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden"
                   onChange={(e) => upload(e.target.files[0])} data-testid="contract-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="contract-upload"
              className="flex items-center gap-2 bg-amber-500 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              <UploadCloud className="h-4 w-4" /> {busy ? "Envoi…" : "Renvoyer signé"}
            </button>
          </div>
          {signMode && (
            <SignaturePad
              onSigned={() => { setSignMode(false); onChanged?.(); }}
              onCancel={() => setSignMode(false)}
            />
          )}
        </>
      )}
    </section>
  );
};
